import { useMajors } from "@/hooks/useMajors";
import { useInterests } from "@/hooks/useInterests";
import { getGroupNames, translateGroupName } from "@/util/funnelGroups";
import { useCounselors } from "@/hooks/useCounselors";
import {
  StudentTypeEnum,
  YesNoEnum,
  JoinTypeEnum,
  GoingStatusEnum,
  CountryClassificationEnum,
  GenderEnum,
  UnitedStatesStateEnum,
  PredictionIndexEnum,
  EntryTermEnum,
  BulkMessageLocationEnum,
  BulkMessageStudentSourceEnum,
  RisePredictiveIndexEnum,
  RisePredictiveModelEnum,
  EngagementPercentileEnum,
  PredictiveScoreTrendEnum,
} from "@/__generated__/graphql";
import { useMemo } from "react";
import { FilterName } from "@/components/FilterSidebar/types";
import { SelectOption } from "@/components/FormInputs/types";
import { CURRENT_COHORT_YEAR } from "@/api/queries/currentCohortYear";
import { useQuery } from "@apollo/client";
import { getCohortYearRange } from "../util/getCohortYearRange";
import { getTranslatedOptionsFromEnums } from "../util/getTranslatedOptionsFromEnum";
import { FafsaCompletionStatusEnum } from "@/__generated__/types";
import { useTranslation } from "react-i18next";
import { getJoinStateGroupNames } from "@/util/joinStateGroups";

export type FilterNameWithOpts = Exclude<
  FilterName,
  | "joinDate"
  | "zipCode"
  | "ceebCode"
  | "leadSource"
  | "inCommunity"
  | "commitDate"
  | "hasProfilePhoto"
>;
interface FilterFieldOptions {
  options: Record<FilterNameWithOpts, SelectOption[]> | null;
  loading: boolean;
}

export const enumFields = {
  entryTerm: EntryTermEnum,
  studentType: StudentTypeEnum,
  commitIndex: PredictionIndexEnum,
  goingStatus: GoingStatusEnum,
  attendedALiveEvent: YesNoEnum,
  joinType: JoinTypeEnum,
  gender: GenderEnum,
  country: CountryClassificationEnum,
  state: UnitedStatesStateEnum,
  directAdmission: YesNoEnum,
  deposited: YesNoEnum,
  firstGeneration: YesNoEnum,
  studentAthlete: YesNoEnum,
  campusVisit: YesNoEnum,
  fafsaCompletionStatus: FafsaCompletionStatusEnum,
  goingElsewhere: YesNoEnum,
  moreActiveElsewhere: YesNoEnum,
  location: BulkMessageLocationEnum,
  studentSource: BulkMessageStudentSourceEnum,
  applyIndex: RisePredictiveIndexEnum,
  riseCommitIndex: RisePredictiveIndexEnum,
  meltIndex: RisePredictiveIndexEnum,
  yieldIndex: RisePredictiveIndexEnum,
  upTrendingScores: RisePredictiveModelEnum,
  downTrendingScores: RisePredictiveModelEnum,
  engagementPercentile: EngagementPercentileEnum,
  commitScoreTrend: PredictiveScoreTrendEnum,
};

const COMMIT_INDEX_ORDER = {
  [PredictionIndexEnum.Low]: 0,
  [PredictionIndexEnum.Medium]: 1,
  [PredictionIndexEnum.High]: 2,
};

const FAFSA_STATUS_ORDER = {
  [FafsaCompletionStatusEnum.Yes]: 0,
  [FafsaCompletionStatusEnum.No]: 1,
  [FafsaCompletionStatusEnum.NonFiler]: 2,
};

const RISE_PREDICTIVE_INDEX_ORDER = {
  [RisePredictiveIndexEnum.High]: 0,
  [RisePredictiveIndexEnum.MediumHigh]: 1,
  [RisePredictiveIndexEnum.Medium]: 2,
  [RisePredictiveIndexEnum.MediumLow]: 3,
  [RisePredictiveIndexEnum.Low]: 4,
};

const RISE_PREDICTIVE_MODEL_ORDER = {
  [RisePredictiveModelEnum.Apply]: 0,
  [RisePredictiveModelEnum.Commit]: 1,
  [RisePredictiveModelEnum.Yield]: 2,
  [RisePredictiveModelEnum.Melt]: 3,
};

const mapNodeToOptions = (
  data?: { name?: string; id?: string }[],
): SelectOption[] =>
  data?.map((item) => ({
    label: item.name || "",
    value: item.id || "",
  })) || [];

export const useFilterFieldOptions = (): FilterFieldOptions => {
  const majorsResp = useMajors();
  const interestsResp = useInterests();
  const counselorsResp = useCounselors();
  const { data: currentCohortYearData, loading: cohortYearLoading } =
    useQuery(CURRENT_COHORT_YEAR);
  const { t } = useTranslation();

  const currentCohortYear = currentCohortYearData?.currentCohortYear;

  const loading =
    majorsResp.loading ||
    interestsResp.loading ||
    counselorsResp.loading ||
    cohortYearLoading;

  const optionsData = useMemo(() => {
    if (!interestsResp.data || !counselorsResp.data || !majorsResp.data) {
      return null;
    }

    const cohortYearOpts = getCohortYearRange(currentCohortYear).map(
      (year) => ({
        label: year,
        value: year,
      }),
    );

    const majorOpts: SelectOption[] = mapNodeToOptions(majorsResp.data);
    const counselorOpts: SelectOption[] = mapNodeToOptions(counselorsResp.data);
    const interestOpts: SelectOption[] = mapNodeToOptions(interestsResp.data);

    const funnelStatesOpts = getGroupNames("SIDEBAR").map((groupName) => ({
      value: groupName,
      label: translateGroupName(groupName),
    }));

    const joinStateOpts = getJoinStateGroupNames().map((optName) => ({
      value: optName,
      label: t(`joinStateOptions.${optName}`),
    }));

    const { enumOptions, missingTranslationKeys } =
      getTranslatedOptionsFromEnums<typeof enumFields>(enumFields);

    if (missingTranslationKeys.length > 0) {
      console.error(
        `Missing translations for graphql enums: ${missingTranslationKeys.join("\n")}`,
      );
    }

    const {
      commitIndex,
      fafsaCompletionStatus,
      applyIndex,
      riseCommitIndex,
      meltIndex,
      yieldIndex,
      upTrendingScores,
      downTrendingScores,
      joinType,
      ...otherEnumOptions
    } = enumOptions;

    return {
      majors: majorOpts,
      funnelStates: funnelStatesOpts,
      assignedCounselors: counselorOpts,
      interests: interestOpts,
      cohortYear: cohortYearOpts,
      commitIndex: sortOption(commitIndex, COMMIT_INDEX_ORDER),
      joinState: joinStateOpts,
      fafsaCompletionStatus: sortOption(
        fafsaCompletionStatus,
        FAFSA_STATUS_ORDER,
      ),
      applyIndex: sortOption(applyIndex, RISE_PREDICTIVE_INDEX_ORDER),
      riseCommitIndex: sortOption(riseCommitIndex, RISE_PREDICTIVE_INDEX_ORDER),
      meltIndex: sortOption(meltIndex, RISE_PREDICTIVE_INDEX_ORDER),
      yieldIndex: sortOption(yieldIndex, RISE_PREDICTIVE_INDEX_ORDER),
      upTrendingScores: sortOption(
        upTrendingScores,
        RISE_PREDICTIVE_MODEL_ORDER,
      ),
      downTrendingScores: sortOption(
        downTrendingScores,
        RISE_PREDICTIVE_MODEL_ORDER,
      ),
      joinType: joinType.map((opt) => {
        if (opt.value === JoinTypeEnum.Csv) {
          return { ...opt, label: t("filters.fieldOptions.COMMUNITY_MATCH") };
        }
        return opt;
      }),
      ...otherEnumOptions,
    };
  }, [
    interestsResp.data,
    counselorsResp.data,
    majorsResp.data,
    currentCohortYear,
    t,
  ]);

  if (loading) {
    return { options: null, loading: true };
  }

  return { options: optionsData, loading };
};

function sortOption(
  options: SelectOption[],
  sortObject: Record<string, number>,
) {
  return options
    .filter((option) => option.value in sortObject)
    .sort(
      (option1, option2) =>
        sortObject[option1.value] - sortObject[option2.value],
    );
}
